import React, { useEffect } from 'react'

import { Dialog, IconButton } from '@mui/material'
import { Close as CloseIcon } from '@mui/icons-material'

import { StartChargingStep } from './hooks/useStartCharging/types'

import { PendingBalanceStep } from './steps/PendingBalanceStep'
import { AmountStep } from './steps/AmountStep'
import { PaymentMethodStep } from './steps/PaymentMethodStep'
import { IdleRateConfirmationStep } from './steps/IdleRateConfirmationStep'
import { ConfirmationStep } from './steps/ConfirmationStep'
import { ProcessPaymentStep } from './steps/ProcessPaymentStep'
import { useStartCharging } from './hooks/useStartCharging/useStartCharging'
import { useDisclosure } from '@/hooks/useDisclosure'
import { useToast } from '@/contexts/ToastContext'
import {
  CHARGING_START_PENDING_STATUSES,
  CHARGING_START_SUCCESS_STATUSES,
} from '@/lib/in-app-notifications/categories/charging_session'
import { AddCardStep } from './steps/AddCardStep'
import { useActiveSession } from '@/contexts/ActiveSessionContext'

export interface StartChargingModalProps {
  modalMethods: ReturnType<typeof useDisclosure>
  startChargingMethods: ReturnType<typeof useStartCharging>
}

export const StartChargingModal: React.FC<StartChargingModalProps> = ({ modalMethods, startChargingMethods }) => {
  const { show } = useToast()
  const { notification } = useActiveSession()
  const notificationStatus = notification?.data?.payload?.status

  const isAwaitingPayment = notificationStatus ? CHARGING_START_PENDING_STATUSES.includes(notificationStatus) : false

  useEffect(() => {
    if (!notificationStatus) return

    const shouldCloseModal = modalMethods?.isOpen && CHARGING_START_SUCCESS_STATUSES.includes(notificationStatus)

    if (shouldCloseModal) {
      show('Sessão iniciada com sucesso!', { severity: 'success' })
      modalMethods.close()
    }
  }, [notificationStatus])

  const renderStep = () => {
    switch (startChargingMethods.state.currentStep) {
      case StartChargingStep.PENDING_BALANCE:
        return <PendingBalanceStep startChargingMethods={startChargingMethods} />
      case StartChargingStep.AMOUNT:
        return <AmountStep startChargingMethods={startChargingMethods} />
      case StartChargingStep.PAYMENT_METHOD:
        return <PaymentMethodStep startChargingMethods={startChargingMethods} />
      case StartChargingStep.IDLE_RATE_CONFIRMATION:
        return <IdleRateConfirmationStep startChargingMethods={startChargingMethods} />
      case StartChargingStep.ADD_CARD:
        return <AddCardStep startChargingMethods={startChargingMethods} />
      case StartChargingStep.CONFIRMATION:
        return <ConfirmationStep startChargingMethods={startChargingMethods} />
      case StartChargingStep.PROCESS_PAYMENT:
        return <ProcessPaymentStep startChargingMethods={startChargingMethods} />
      default:
        return null
    }
  }

  return (
    <Dialog
      open={modalMethods.isOpen}
      onClose={isAwaitingPayment ? undefined : startChargingMethods.cancel}
      fullWidth
      slotProps={{
        paper: {
          sx: { borderRadius: 3, padding: 1, minWidth: 320 },
        },
      }}
    >
      {!isAwaitingPayment && (
        <IconButton
          aria-label="fechar"
          onClick={startChargingMethods.cancel}
          disabled={startChargingMethods.state.isProcessing}
          sx={{
            position: 'absolute',
            right: 4,
            top: 4,
          }}
        >
          <CloseIcon color="disabled" sx={{ fontSize: '16px' }} />
        </IconButton>
      )}
      {renderStep()}
    </Dialog>
  )
}
